import React, { useState, useEffect, useRef } from "react";
import {
  Cloud,
  Loader,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Plus,
  Edit2,
  CheckCircle,
  X,
  Layers,
  Trash2,
} from "lucide-react";
import {
  listCloudProviders,
  CloudProviderResponse,
  getAwsMappingHierarchy,
  buildAndMapComponents,
  BuildAndMapResult,
  createAwsMapping,
  updateAwsMapping,
  deleteAwsMapping,
  updateAwsResourceMapping,
  bulkUpdateAwsResourceMappings,
  AwsServiceHierarchyItem,
  AwsResourceMappingResponse,
  unlinkCloudAccountFromApp,
  linkCloudAccountToApp,
  listCMDBComponents,
  CMDBComponent,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction } from "../components/RBACButton";
import { Modal, FormField, ModalFooterButtons } from "../components/Modal";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { StatusBadge } from "./cmdb/cmdbUtils";
import { ComponentPicker } from "./cmdb/components/ComponentPicker";
import { Tip } from "../components/ui/tooltip";

// ── Resource status colour mapping ───────────────────────────────────────────
function mapAwsStatus(status: string | null): string {
  if (!status) return "UNKNOWN";
  const s = status.toLowerCase();
  if (["running", "available", "active", "in-use"].includes(s))
    return "RUNNING";
  if (["stopped", "failed", "terminated", "deleted"].includes(s))
    return "STOPPED";
  if (["pending", "starting", "creating", "modifying"].includes(s))
    return "STARTING";
  return "UNKNOWN";
}

type MapFilter = "ALL" | "MAPPED" | "UNMAPPED";

interface Props {
  applicationId: string;
  onAccountUnlinked?: () => void;
}

export function CMDBAwsMapping({ applicationId, onAccountUnlinked }: Props) {
  // ── Account state ────────────────────────────────────────────────────────
  const [accounts, setAccounts] = useState<CloudProviderResponse[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // ── Hierarchy state ──────────────────────────────────────────────────────
  const [hierarchy, setHierarchy] = useState<AwsServiceHierarchyItem[]>([]);
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(false);
  const [isRescanning, setIsRescanning] = useState(false);
  const [isBuildingMap, setIsBuildingMap] = useState(false);
  const [buildMapResult, setBuildMapResult] = useState<BuildAndMapResult | null>(null);
  const [filter, setFilter] = useState<MapFilter>("ALL");
  const [hierarchySearch, setHierarchySearch] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // ── Add Account modal (select existing) ─────────────────────────────────
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [allAccounts, setAllAccounts] = useState<CloudProviderResponse[]>([]);
  const [selectedAddAccount, setSelectedAddAccount] =
    useState<CloudProviderResponse | null>(null);
  const [loadingAllAccounts, setLoadingAllAccounts] = useState(false);

  // ── Unlink account confirmation ───────────────────────────────────────────
  const [confirmUnlinkProvider, setConfirmUnlinkProvider] =
    useState<CloudProviderResponse | null>(null);
  const [isUnlinkingAccount, setIsUnlinkingAccount] = useState(false);

  // ── Component picker (inline popover per service row) ────────────────────
  const [showPickerFor, setShowPickerFor] = useState<string | null>(null);
  const [pickerSelectedComp, setPickerSelectedComp] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [pickerSelectedResourceIds, setPickerSelectedResourceIds] = useState<
    Set<string>
  >(new Set());
  const [pickerSaving, setPickerSaving] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // ── Resource edit dialog ─────────────────────────────────────────────────
  const [showResDialog, setShowResDialog] = useState(false);
  const [editingRes, setEditingRes] = useState<{
    svc: AwsServiceHierarchyItem;
    res: AwsResourceMappingResponse;
  } | null>(null);
  const [resExclude, setResExclude] = useState(false);
  const [resComp, setResComp] = useState<{ id: string; title: string } | null>(
    null,
  );
  const [resSaving, setResSaving] = useState(false);

  // ── Load accounts on mount ───────────────────────────────────────────────
  useEffect(() => {
    setLoadingAccounts(true);
    listCloudProviders({ applicationId })
      .then((r) => {
        const aws = (r.data ?? []).filter((p) => p.provider === "AWS");
        setAccounts(aws);
        if (aws.length > 0) setSelectedAccountId(aws[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingAccounts(false));
  }, [applicationId]);

  // ── Load hierarchy when selected account changes ─────────────────────────
  useEffect(() => {
    if (!selectedAccountId) {
      setHierarchy([]);
      return;
    }
    loadHierarchy(selectedAccountId);
  }, [selectedAccountId]);

  function loadHierarchy(accountId: string) {
    setIsLoadingHierarchy(true);
    getAwsMappingHierarchy(applicationId, accountId)
      .then((r) => setHierarchy(r.data ?? []))
      .catch(() =>
        useToastStore
          .getState()
          .showError("Error", "Failed to load service mappings"),
      )
      .finally(() => setIsLoadingHierarchy(false));
  }

  async function refetchAccounts(): Promise<CloudProviderResponse[]> {
    try {
      const r = await listCloudProviders({ applicationId });
      const aws = (r.data ?? []).filter((p) => p.provider === "AWS");
      setAccounts(aws);
      return aws;
    } catch {
      return accounts;
    }
  }

  // ── Auto-Link Components ─────────────────────────────────────────────────
  async function handleBuildAndMap() {
    if (!selectedAccountId) return;
    setIsBuildingMap(true);
    setBuildMapResult(null);
    try {
      const res = await buildAndMapComponents(applicationId, selectedAccountId);
      const r = res.data;
      setBuildMapResult(r);
      useToastStore.getState().showSuccess(
        "Auto-Link complete",
        `Linked ${r.autoMatched} services${r.unmatched > 0 ? `, ${r.unmatched} need attention` : ""}.`,
      );
      loadHierarchy(selectedAccountId);
    } catch (err) {
      useToastStore.getState().showError(
        "Auto-Link failed",
        err instanceof Error ? err.message : "Failed to auto-link components",
      );
    } finally {
      setIsBuildingMap(false);
    }
  }

  // ── Open Add Account modal — fetch all accounts ───────────────────────────
  async function openAddModal() {
    setIsAddModalOpen(true);
    setSelectedAddAccount(null);
    setLoadingAllAccounts(true);
    try {
      const r = await listCloudProviders();
      setAllAccounts((r.data ?? []).filter((p) => p.provider === "AWS"));
    } catch {
      useToastStore
        .getState()
        .showError("Error", "Failed to load cloud accounts");
    } finally {
      setLoadingAllAccounts(false);
    }
  }

  // ── Link selected account to this application ─────────────────────────────
  async function handleLinkAccount() {
    if (!selectedAddAccount) return;
    setIsSaving(true);
    try {
      await linkCloudAccountToApp(applicationId, selectedAddAccount.id);
      const scanRes = await buildAndMapComponents(
        applicationId,
        selectedAddAccount.id,
      );
      const r = scanRes.data;
      const unmapped = r.unmatched ?? (r.scanned - r.autoMatched - r.alreadyMapped);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Account linked — ${r.autoMatched} linked, ${unmapped} not linked`,
        );
      setIsAddModalOpen(false);
      setSelectedAddAccount(null);

      await refetchAccounts();
      setSelectedAccountId(selectedAddAccount.id);
      loadHierarchy(selectedAddAccount.id);
    } catch (err: any) {
      useToastStore
        .getState()
        .showError("Error", err?.message || "Failed to link account");
    } finally {
      setIsSaving(false);
    }
  }

  // ── Unlink cloud account from application ────────────────────────────────
  async function handleConfirmUnlinkAccount() {
    if (!confirmUnlinkProvider) return;
    setIsUnlinkingAccount(true);
    try {
      await unlinkCloudAccountFromApp(applicationId, confirmUnlinkProvider.id);
      useToastStore
        .getState()
        .showSuccess("Success", "Cloud account unlinked successfully");

      if (selectedAccountId === confirmUnlinkProvider.id) {
        const remaining = accounts.filter(
          (a) => a.id !== confirmUnlinkProvider.id,
        );
        setSelectedAccountId(remaining[0]?.id ?? null);
        if (remaining.length === 0) setHierarchy([]);
      }

      setConfirmUnlinkProvider(null);
      await refetchAccounts();
      onAccountUnlinked?.();
    } catch (err: any) {
      useToastStore
        .getState()
        .showError("Error", "Action failed — please try again");
    } finally {
      setIsUnlinkingAccount(false);
    }
  }

  // ── Close picker on outside click ────────────────────────────────────────
  useEffect(() => {
    function h(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPickerFor(null);
      }
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  function openPicker(svc: AwsServiceHierarchyItem) {
    setShowPickerFor(svc.cloudServiceId || svc.awsServiceId || "");
    setPickerSelectedComp(
      svc.componentId
        ? { id: svc.componentId, title: svc.componentTitle ?? "" }
        : null,
    );
    setPickerSelectedResourceIds(
      new Set(
        (svc.resources ?? [])
          .filter((r) => !!r.componentId)
          .map((r) => r.resourceId),
      ),
    );
  }

  function togglePickerResource(resourceId: string) {
    setPickerSelectedResourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }

  // ── Save picker (Map or Edit) ────────────────────────────────────────────
  async function handleSavePicker(svc: AwsServiceHierarchyItem) {
    if (!pickerSelectedComp) return;
    if (
      (svc.resources?.length ?? 0) > 0 &&
      pickerSelectedResourceIds.size === 0
    ) {
      showToast("error", "Select at least one resource to map.");
      return;
    }
    setPickerSaving(true);
    try {
      const serviceKey = svc.cloudServiceId || svc.awsServiceId || "";
      let mappingId = svc.mappingId;

      if (!mappingId) {
        await createAwsMapping({
          applicationId,
          cloudProviderId: selectedAccountId!,
          source: "MANUAL",
          cloudServiceId: serviceKey,
          // Keep service-level mapping neutral; resources are explicitly assigned below.
          componentId: null,
          notes: null,
        });
      } else {
        // Ensure service-level fallback does not silently map all resources.
        await updateAwsMapping(mappingId, {
          componentId: null,
          notes: null,
          source: "MANUAL",
        });
      }

      const fresh = await getAwsMappingHierarchy(
        applicationId,
        selectedAccountId!,
      );
      const latestSvc = (fresh.data ?? []).find(
        (s) => (s.cloudServiceId || s.awsServiceId || "") === serviceKey,
      );
      if (!latestSvc?.mappingId) {
        showToast("error", "Service mapping not ready. Please retry.");
        return;
      }
      mappingId = latestSvc.mappingId;

      await bulkUpdateAwsResourceMappings(mappingId, {
        componentId: pickerSelectedComp.id,
        selectedResourceIds: Array.from(pickerSelectedResourceIds),
        replace: true,
      });
      if ((latestSvc.resources ?? []).length === 0) {
        // Fallback for service groups without resource rows.
        await updateAwsMapping(mappingId, {
          componentId: pickerSelectedComp.id,
          notes: null,
          source: "MANUAL",
        });
      }
      useToastStore.getState().showSuccess("Success", "Component assigned");
      setShowPickerFor(null);
      loadHierarchy(selectedAccountId!);
    } catch {
      useToastStore.getState().showError("Error", "Failed to assign component");
    } finally {
      setPickerSaving(false);
    }
  }

  // ── Unlink service mapping ───────────────────────────────────────────────
  async function handleUnlink(svc: AwsServiceHierarchyItem) {
    if (!svc.mappingId) return;
    try {
      await deleteAwsMapping(svc.mappingId);
      useToastStore.getState().showSuccess("Success", "Mapping removed");
      setShowPickerFor(null);
      loadHierarchy(selectedAccountId!);
    } catch {
      useToastStore.getState().showError("Error", "Failed to remove mapping");
    }
  }

  // ── Resource edit dialog ─────────────────────────────────────────────────
  function openResDialog(
    svc: AwsServiceHierarchyItem,
    res: AwsResourceMappingResponse,
  ) {
    setEditingRes({ svc, res });
    setResExclude(res.isExcluded);
    setResComp(
      res.componentId
        ? { id: res.componentId, title: res.componentTitle ?? "" }
        : null,
    );
    setShowResDialog(true);
  }

  async function handleSaveResource() {
    if (!editingRes || !editingRes.svc.mappingId || !editingRes.res.mappingId) {
      useToastStore
        .getState()
        .showError("Error", "No resource mapping found. Run Auto-Link Components first.");
      return;
    }
    setResSaving(true);
    try {
      const updated = await updateAwsResourceMapping(
        editingRes.svc.mappingId,
        editingRes.res.mappingId,
        {
          componentId: resComp?.id ?? null,
          isExcluded: resExclude,
          notes: null,
          source: "MANUAL",
        },
      );
      setHierarchy((prev) =>
        prev.map((s) =>
          (s.cloudServiceId || s.awsServiceId) ===
          (editingRes.svc.cloudServiceId || editingRes.svc.awsServiceId)
            ? {
                ...s,
                resources: s.resources.map((r) =>
                  r.resourceId === editingRes.res.resourceId ? updated.data : r,
                ),
              }
            : s,
        ),
      );
      useToastStore.getState().showSuccess("Success", "Resource updated");
      setShowResDialog(false);
    } catch {
      useToastStore.getState().showError("Error", "Failed to update resource");
    } finally {
      setResSaving(false);
    }
  }

  // ── Derived counts & filtered list ───────────────────────────────────────
  const mappedCount = hierarchy.filter((s) =>
    (s.resources ?? []).some((r) => !!r.componentId),
  ).length;
  const unmappedCount = hierarchy.length - mappedCount;

  const visible = (() => {
    let list =
      filter === "MAPPED"
        ? hierarchy.filter((s) => !!s.componentId)
        : filter === "UNMAPPED"
          ? hierarchy.filter((s) => !s.componentId)
          : hierarchy;
    if (hierarchySearch) {
      const q = hierarchySearch.toLowerCase();
      list = list.filter(
        (s) =>
          (s.serviceName ?? "").toLowerCase().includes(q) ||
          (s.componentTitle ?? "").toLowerCase().includes(q),
      );
    }
    return list;
  })();

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  if (loadingAccounts) {
    return (
      <div className="flex items-center justify-center py-16 d gap-2">
        <Loader className="size-5 animate-spin" />
        <span className="text-sm">Loading accounts…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-border rounded-xl text-center">
          <div className="size-14 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center mb-4">
            <Cloud className="size-7 text-indigo-500" />
          </div>
          <p className="text-base font-semibold text-foreground mb-1">
            No cloud accounts linked
          </p>
          <p className="text-sm d mb-6">
            Link an account to auto-discover services
          </p>
          <button
            onClick={() => openAddModal()}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            <Plus className="size-4" />
            Add Cloud Account
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 p-1 bg-muted rounded-lg">
                {accounts.map((acc) => (
                  <div
                    key={acc.id}
                    className={`group flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      acc.id === selectedAccountId
                        ? "bg-background text-foreground shadow-sm"
                        : "d hover:text-foreground"
                    }`}
                  >
                    <button
                      onClick={() => setSelectedAccountId(acc.id)}
                      className="flex items-center gap-2 min-w-0"
                    >
                      <Cloud className="size-3.5 flex-shrink-0" />
                      <span>{acc.accountName}</span>
                    </button>
                    <Tip label="Unlink account" side="top">
                      <button
                        onClick={() => setConfirmUnlinkProvider(acc)}
                        className="opacity-0 group-hover:opacity-100 ml-0.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-400 transition-all p-0.5"
                      >
                        <X className="size-3" />
                      </button>
                    </Tip>
                  </div>
                ))}
              </div>
              <button
                onClick={() => openAddModal()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-500/40 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors font-medium"
              >
                <Plus className="size-4" />
                Add Account
              </button>
            </div>

            <button
              onClick={handleBuildAndMap}
              disabled={isBuildingMap || !selectedAccountId}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium"
            >
              <Layers className={`size-4 ${isBuildingMap ? "animate-pulse" : ""}`} />
              {isBuildingMap ? "Linking…" : "Auto-Link Components"}
            </button>
          </div>

          {/* Unmatched warning */}
          {buildMapResult && buildMapResult.unmatched > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
              <span className="font-semibold">{buildMapResult.unmatched} service{buildMapResult.unmatched !== 1 ? "s" : ""} need attention</span>
              <span>— use the manual linking below to assign components.</span>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 d" />
              <input
                type="text"
                placeholder="Search services…"
                value={hierarchySearch}
                onChange={(e) => setHierarchySearch(e.target.value)}
                className="pl-10 pr-4 py-1.5 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm w-56"
              />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="d">
                <span className="font-semibold text-foreground">
                  {hierarchy.length}
                </span>{" "}
                Total
              </span>
              <span className="d">
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {mappedCount}
                </span>{" "}
                Linked
              </span>
              <span className="d">
                <span className="font-semibold text-amber-600 dark:text-amber-400">
                  {unmappedCount}
                </span>{" "}
                Not linked
              </span>
            </div>
            <div className="flex items-center gap-1">
              {(["ALL", "MAPPED", "UNMAPPED"] as MapFilter[]).map((chip) => (
                <button
                  key={chip}
                  onClick={() => setFilter(chip)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filter === chip
                      ? "bg-indigo-600 text-white"
                      : "bg-muted d hover:bg-muted/80"
                  }`}
                >
                  {chip === "ALL"
                    ? "All"
                    : chip === "MAPPED"
                      ? "Linked"
                      : "Not linked"}
                </button>
              ))}
            </div>
          </div>

          {isLoadingHierarchy ? (
            <div className="flex items-center justify-center py-12 d gap-2">
              <Loader className="size-5 animate-spin" />
              <span className="text-sm">Loading services…</span>
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12 d text-sm border border-dashed border-border rounded-lg">
              {hierarchy.length === 0
                ? "No services found — click Auto-Link Components to discover services."
                : "No services match the selected filter."}
            </div>
          ) : (
            <div className="space-y-2">
              {visible.map((svc) => {
                const serviceKey = svc.cloudServiceId || svc.awsServiceId || "";
                const isExpanded = expandedIds.has(serviceKey);
                const isMapped = (svc.resources ?? []).some(
                  (r) => !!r.componentId,
                );
                const isPickerOpen = showPickerFor === serviceKey;

                return (
                  <div
                    key={serviceKey}
                    className="border border-border rounded-lg bg-card"
                  >
                    <div
                      onClick={() => {
                        if (svc.resources.length > 0) toggleExpand(serviceKey);
                      }}
                      className={`flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors ${
                        svc.resources.length > 0 ? "cursor-pointer" : ""
                      }`}
                    >
                      <button
                        type="button"
                        disabled={svc.resources.length === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (svc.resources.length > 0) toggleExpand(serviceKey);
                        }}
                        aria-expanded={isExpanded}
                        className="flex-shrink-0 p-0.5 rounded transition-colors d disabled:opacity-30"
                      >
                        {isExpanded ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </button>

                      <div className="flex-shrink-0 size-7 rounded-md bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center">
                        <Cloud className="size-4 text-indigo-600 dark:text-indigo-400" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-foreground truncate">
                            {svc.serviceName}
                          </span>
                          {svc.isGlobal && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 font-semibold flex-shrink-0">
                              GLOBAL
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs d">
                          <span>{svc.category}</span>
                          {svc.resources.length > 0 && (
                            <span>
                              · {svc.resources.length} resource
                              {svc.resources.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0 flex items-center gap-2 relative">
                        {isMapped ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 text-xs font-semibold whitespace-nowrap">
                            <CheckCircle className="size-3 flex-shrink-0" />
                            Linked → {svc.componentTitle}
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full bg-muted d text-xs font-semibold">
                            Not linked
                          </span>
                        )}

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openPicker(svc);
                          }}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            isMapped
                              ? "border border-border hover:bg-muted text-foreground"
                              : "bg-indigo-600 text-white hover:bg-indigo-700"
                          }`}
                        >
                          {isMapped ? (
                            <>
                              <Edit2 className="size-3" />
                              Edit
                            </>
                          ) : (
                            <>
                              <Plus className="size-3" />
                              Link
                            </>
                          )}
                        </button>

                        {isPickerOpen && (
                          <div
                            ref={pickerRef}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 top-full mt-1 z-50 w-72 bg-popover border border-border rounded-xl shadow-xl p-3 space-y-3"
                          >
                            <FormField label="Assign Component" required>
                              <ComponentPicker
                                value={pickerSelectedComp?.id || ""}
                                title={pickerSelectedComp?.title || ""}
                                onChange={(id, title) =>
                                  setPickerSelectedComp({ id, title })
                                }
                              />
                            </FormField>
                            {svc.resources.length > 0 && (
                              <div className="space-y-1.5">
                                <p className="text-xs font-medium text-foreground">
                                  Select Resources
                                </p>
                                <div className="max-h-40 overflow-auto rounded-lg border border-border divide-y divide-border/40">
                                  {svc.resources.map((res) => {
                                    const checked =
                                      pickerSelectedResourceIds.has(
                                        res.resourceId,
                                      );
                                    return (
                                      <label
                                        key={res.resourceId}
                                        className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-muted/30"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() =>
                                            togglePickerResource(res.resourceId)
                                          }
                                          className="accent-indigo-600"
                                        />
                                        <span className="truncate text-foreground">
                                          {res.resourceName ||
                                            res.resourceIdentifier ||
                                            res.resourceId}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                                <p className="text-[11px] d">
                                  Only selected resources will be mapped to the
                                  chosen component.
                                </p>
                              </div>
                            )}

                            <div className="flex items-center gap-2 pt-1 border-t border-border">
                              {isMapped && (
                                <RBACAction module="cmdb" action="delete">
                                  <Tip label="Unlink" side="top">
                                    <button
                                      onClick={() => handleUnlink(svc)}
                                      className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors"
                                    >
                                      <Trash2 className="size-4" />
                                    </button>
                                  </Tip>
                                </RBACAction>
                              )}
                              <button
                                onClick={() => setShowPickerFor(null)}
                                className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-muted transition-colors ml-auto"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleSavePicker(svc)}
                                disabled={!pickerSelectedComp || pickerSaving}
                                className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                              >
                                {pickerSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {isExpanded &&
                      svc.resources.length > 0 &&
                      (() => {
                        const hasName = svc.resources.some(
                          (r) => !!r.resourceName,
                        );
                        const hasAZ =
                          !svc.isGlobal &&
                          svc.resources.some((r) => !!r.availabilityZone);
                        return (
                          <div className="border-t border-border/50 bg-muted/10 overflow-x-auto rounded-b-lg">
                            <table className="w-full text-xs min-w-[600px]">
                              <thead>
                                <tr className="d tracking-wider text-[10px] border-b border-border divide-x divide-border">
                                  {hasName && (
                                    <th className="px-4 py-2 text-left">
                                      Resource
                                    </th>
                                  )}
                                  <th className="px-4 py-2 text-center">
                                    Identifier
                                  </th>
                                  <th className="px-4 py-2 text-center">
                                    Region
                                  </th>
                                  {hasAZ && (
                                    <th className="px-4 py-2 text-center">AZ</th>
                                  )}
                                  <th className="px-4 py-2 text-center">
                                    Status
                                  </th>
                                  <th className="px-4 py-2 text-center">
                                    Component
                                  </th>
                                  <th className="px-4 py-2 text-center">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border/30">
                                {svc.resources.map((res) => (
                                  <tr
                                    key={res.resourceId}
                                    className={`hover:bg-muted/20 transition-colors divide-x divide-border ${res.isExcluded ? "opacity-50" : ""}`}
                                  >
                                    {hasName && (
                                      <td className="px-4 py-2 text-foreground text-left">
                                        <span
                                          className={
                                            res.isExcluded
                                              ? "line-through d"
                                              : ""
                                          }
                                        >
                                          {res.resourceName ?? "—"}
                                        </span>
                                      </td>
                                    )}
                                    <td className="px-4 py-2 font-mono d text-[10px] max-w-[160px] text-center">
                                      <Tip
                                        label={res.resourceIdentifier ?? ""}
                                        side="top"
                                        className="block truncate"
                                      >
                                        {res.resourceIdentifier ?? "—"}
                                      </Tip>
                                    </td>
                                    <td className="px-4 py-2 d text-center">
                                      {res.region ?? "—"}
                                    </td>
                                    {hasAZ && (
                                      <td className="px-4 py-2 d text-center">
                                        {res.availabilityZone ?? "—"}
                                      </td>
                                    )}
                                    <td className="px-4 py-2 text-center">
                                      <StatusBadge
                                        status={mapAwsStatus(res.status)}
                                        label={res.status || "Unknown"}
                                      />
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      {res.componentTitle ? (
                                        <span className="flex items-center justify-center gap-1.5">
                                          <span className="text-foreground">
                                            {res.componentTitle}
                                          </span>
                                          {res.source && (
                                            <span
                                              className={`px-1 py-0.5 rounded text-[9px] font-semibold ${
                                                res.source === "AUTO"
                                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                                                  : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                                              }`}
                                            >
                                              {res.source === "AUTO" ? "Auto-linked" : "Manually linked"}
                                            </span>
                                          )}
                                        </span>
                                      ) : (
                                        <span className="d italic">
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <Tip label="Edit Resource Mapping" side="top">
                                        <button
                                          onClick={() =>
                                            openResDialog(svc, res)
                                          }
                                          className="p-1 rounded hover:bg-muted d transition-colors"
                                        >
                                          <Edit2 className="size-3.5" />
                                        </button>
                                        </Tip>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Add Account Modal ──────────────────────────────────────────────── */}
      <Modal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Link AWS Account"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setIsAddModalOpen(false)}
            onSubmit={handleLinkAccount}
            submitLabel={isSaving ? "Linking…" : "Link Account"}
            submitDisabled={!selectedAddAccount || isSaving}
          />
        }
      >
        <div className="space-y-4">
          <p className="text-sm d">
            Select an AWS account to discover its resources for this
            application.
          </p>
          <div className="max-h-60 overflow-y-auto space-y-1.5 border border-border rounded-lg p-1">
            {loadingAllAccounts ? (
              <div className="flex justify-center py-8">
                <Loader className="size-5 animate-spin d" />
              </div>
            ) : allAccounts.length === 0 ? (
              <p className="text-center py-8 text-sm d">
                No AWS accounts available. Add one in Global Settings.
              </p>
            ) : (
              allAccounts.map((acc) => (
                <button
                  key={acc.id}
                  onClick={() => setSelectedAddAccount(acc)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedAddAccount?.id === acc.id
                      ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-500"
                      : "border-border hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-foreground">
                      {acc.accountName}
                    </span>
                    <span className="text-xs d font-mono">{acc.accountId}</span>
                  </div>
                  <p className="text-xs d mt-1">
                    Region: {acc.region || "Global"}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      </Modal>

      {/* ── Unlink Confirmation ────────────────────────────────────────────── */}
      <DeleteConfirmDialog
        open={!!confirmUnlinkProvider}
        type="Cloud Account"
        name={confirmUnlinkProvider?.accountName || ""}
        deleting={isUnlinkingAccount}
        onCancel={() => setConfirmUnlinkProvider(null)}
        onConfirm={handleConfirmUnlinkAccount}
      />

      {/* ── Resource Edit Dialog ───────────────────────────────────────────── */}
      <Modal
        open={showResDialog}
        onClose={() => setShowResDialog(false)}
        title="Resource Mapping"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowResDialog(false)}
            onSubmit={handleSaveResource}
            submitLabel={resSaving ? "Saving…" : "Save Changes"}
            submitDisabled={resSaving}
          />
        }
      >
        {editingRes && (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold d tracking-wider mb-2">
                Resource Info
              </p>
              <div className="p-3 bg-muted/50 rounded-lg space-y-1">
                <p className="text-sm font-medium">
                  {editingRes.res.resourceName || "Unnamed Resource"}
                </p>
                <p className="text-xs font-mono d break-all">
                  {editingRes.res.resourceIdentifier}
                </p>
              </div>
            </div>

            <FormField label="Specific Component">
              <ComponentPicker
                value={resComp?.id || ""}
                title={resComp?.title || ""}
                onChange={(id, title) => setResComp({ id, title })}
              />
            </FormField>
          </div>
        )}
      </Modal>
    </div>
  );
}
